import {
  Instagram,
  Linkedin,
  type LucideIcon,
  Mail,
  MessageCircle,
  MessageSquare,
  Paperclip,
  Phone,
  UserCheck,
  UserPlus,
} from "lucide-react"

import type { ChannelLabelKey } from "@/lib/apps/autoleads/channel-labels"
import type { CampaignPluginFlag } from "@/lib/apps/autoleads/plugin-refs"

import type {
  ChannelManifest,
  ChannelPluginSlug,
  ChannelSlug,
} from "@/nango/types/apps.autoleads/autoleads/Channel"
import type {
  PipelineStep,
  PipelineStepType,
} from "@/nango/types/apps.autoleads/autoleads/MailingPipeline"

// Presentation side of the backend channel registry (apps/autoleads/channels/):
// icons, tones, labels and links. The rules (limits, ordering, reach) stay on
// the server and come through `useChannelManifest`. Adding a channel is one
// entry here, its step types in PipelineStepType, a spread line in STEP_META,
// and its labels in CHANNEL_LABEL_T / STEP_LABEL_T.

// The template family a copy-bearing step drafts into: `step1..stepK` for
// emails, `li1..liM` for LinkedIn messages (`ig`, `wa` for Instagram and
// WhatsApp, `call` for call scripts), each numbered over its own steps.
export type CopyPrefix = "step" | "call" | "li" | "ig" | "wa"

export type CopyFamily = {
  prefix: CopyPrefix
  hasSubject: boolean
  // A contact never drafted for this step can get one written on demand.
  redraftable: boolean
  // Drafted only once one of its blocks is written: blank, the step stays a
  // bare action and never makes the template incomplete (a call with no script).
  optional: boolean
  // Read aloud rather than pasted: a call script, which comes with the
  // prospect's pain points and has nothing to copy into a mailbox.
  script: boolean
  // Its steps share one block and one drafted copy by default (every call
  // reads call1), unless the sequence flags them `script_per_call`.
  shareable: boolean
  // Hard ceiling on a drafted body, null when none. Mirrors the family's
  // `max_chars` server-side (MAILING_*_MAX_CHARS).
  maxChars: number | null
}

// `labelKey` / `hintKey` / `templateToggleKey` resolve against PRODUCT_T;
// `columnKey` against the board's own map. Tones are design tokens only.
export type StepMetaShape = {
  Icon: LucideIcon
  tone: string
  labelKey: string
  hintKey: string
  // Null when the step carries no drafted copy, so no template block.
  templateToggleKey: string | null
  // Whether the step's title is numbered inside its own family ("Email 2").
  numbered: boolean
  copy: CopyFamily | null
}

export type ChannelMeta = {
  Icon: LucideIcon
  tone: string
  labelKey: ChannelLabelKey
  hintKey: ChannelLabelKey
  // Where the rep acts on the channel's target (an address, a number, a URL).
  deepLink: (target: string) => string
  steps: Partial<Record<PipelineStepType, StepMetaShape>>
  // Campaign flags the channel needs on: its targets come from these plugins.
  requiredPluginFlags: readonly CampaignPluginFlag[]
  // A paid add-on channel rather than one every team has.
  extra: boolean
  // The team can connect its own account on it (Integrations tab): its steps
  // then send themselves instead of being manual tasks.
  connectsAccount: boolean
  // The channel's real brand logo: a local asset when the favicon reads badly
  // (WhatsApp's sits on a white square), else the site whose favicon it is.
  // Neither: the channel icon stands in.
  logoSrc: string | null
  logoDomain: string | null
}

// `as const` keeps the translation keys as literals so `useTr` can check them
// against the translation map; `satisfies` still enforces the shape.
export const CHANNELS = {
  email: {
    logoSrc: null,
    logoDomain: null,
    Icon: Mail,
    tone: "bg-success/10 text-success",
    labelKey: "channelEmail",
    hintKey: "channelEmailHint",
    deepLink: (target: string) => `mailto:${target}`,
    steps: {
      email: {
        Icon: Mail,
        tone: "bg-success/10 text-success",
        labelKey: "pipelineStepEmail",
        hintKey: "pipelineEmailStepHint",
        templateToggleKey: "pipelineTemplateToggle",
        numbered: true,
        copy: {
          prefix: "step",
          hasSubject: true,
          redraftable: false,
          optional: false,
          script: false,
          shareable: false,
          maxChars: null,
        },
      },
    },
    requiredPluginFlags: [],
    extra: false,
    connectsAccount: false,
  },
  phone: {
    logoSrc: null,
    logoDomain: null,
    Icon: Phone,
    // Never red: red means late, and a call step is not a problem.
    tone: "bg-foreground/10 text-foreground",
    labelKey: "channelPhone",
    hintKey: "channelPhoneHint",
    deepLink: (target: string) => `tel:${target}`,
    steps: {
      call: {
        Icon: Phone,
        tone: "bg-foreground/10 text-foreground",
        labelKey: "pipelineStepCall",
        hintKey: "pipelineCallStepHint",
        templateToggleKey: "pipelineCallTemplateToggle",
        numbered: true,
        copy: {
          prefix: "call",
          hasSubject: false,
          redraftable: true,
          optional: true,
          script: true,
          shareable: true,
          maxChars: 3500,
        },
      },
    },
    requiredPluginFlags: [],
    extra: false,
    connectsAccount: false,
  },
  linkedin: {
    logoSrc: null,
    logoDomain: "linkedin.com",
    Icon: Linkedin,
    tone: "bg-primary/10 text-primary",
    labelKey: "channelLinkedin",
    hintKey: "channelLinkedinHint",
    // The profile URL is the link itself.
    deepLink: (target: string) => target,
    steps: {
      linkedin_invite: {
        Icon: UserPlus,
        tone: "bg-primary/10 text-primary",
        labelKey: "pipelineStepLinkedinInvite",
        hintKey: "pipelineLinkedinInviteHint",
        templateToggleKey: null,
        numbered: false,
        copy: null,
      },
      linkedin_invite_check: {
        Icon: UserCheck,
        tone: "bg-muted text-muted-foreground",
        labelKey: "pipelineStepLinkedinInviteCheck",
        hintKey: "pipelineLinkedinInviteCheckHint",
        templateToggleKey: null,
        numbered: false,
        copy: null,
      },
      linkedin_message: {
        Icon: MessageSquare,
        tone: "bg-primary/10 text-primary",
        labelKey: "pipelineStepLinkedinMessage",
        hintKey: "pipelineLinkedinMessageHint",
        templateToggleKey: "pipelineLinkedinTemplateToggle",
        numbered: true,
        copy: {
          prefix: "li",
          hasSubject: false,
          redraftable: true,
          optional: false,
          script: false,
          shareable: false,
          maxChars: 900,
        },
      },
    },
    // The crawl never surfaces a person's profile: these plugins do.
    requiredPluginFlags: ["socials_enabled", "decision_makers_enabled"],
    extra: false,
    connectsAccount: true,
  },
  instagram: {
    logoSrc: null,
    logoDomain: "instagram.com",
    Icon: Instagram,
    tone: "bg-primary-cta/10 text-primary-cta-text",
    labelKey: "channelInstagram",
    hintKey: "channelInstagramHint",
    // The canonical profile URL is the link itself.
    deepLink: (target: string) => target,
    steps: {
      instagram_message: {
        Icon: Instagram,
        tone: "bg-primary-cta/10 text-primary-cta-text",
        labelKey: "pipelineStepInstagramMessage",
        hintKey: "pipelineInstagramMessageHint",
        templateToggleKey: "pipelineInstagramTemplateToggle",
        numbered: true,
        copy: {
          prefix: "ig",
          hasSubject: false,
          redraftable: false,
          optional: false,
          script: false,
          shareable: false,
          maxChars: 1000,
        },
      },
    },
    // The crawl never surfaces an Instagram account: the socials plugin does.
    requiredPluginFlags: ["socials_enabled"],
    extra: true,
    connectsAccount: true,
  },
  whatsapp: {
    logoSrc: "/autoleads/logos/whatsapp.svg",
    logoDomain: null,
    Icon: MessageCircle,
    tone: "bg-success/10 text-success-text",
    labelKey: "channelWhatsapp",
    hintKey: "channelWhatsappHint",
    // A chat link takes the number's digits alone, without the "+".
    deepLink: (target: string) => `https://wa.me/${target.replace(/\D/g, "")}`,
    steps: {
      whatsapp_message: {
        Icon: MessageCircle,
        tone: "bg-success/10 text-success-text",
        labelKey: "pipelineStepWhatsappMessage",
        hintKey: "pipelineWhatsappMessageHint",
        templateToggleKey: "pipelineWhatsappTemplateToggle",
        numbered: true,
        copy: {
          prefix: "wa",
          hasSubject: false,
          redraftable: false,
          optional: false,
          script: false,
          shareable: false,
          maxChars: 1000,
        },
      },
    },
    requiredPluginFlags: [],
    extra: true,
    connectsAccount: true,
  },
} as const satisfies Record<ChannelSlug, ChannelMeta>

// Steps that belong to no channel: a file drop feeding the next email.
export const UTILITY_STEPS = {
  file_upload: {
    Icon: Paperclip,
    tone: "bg-warning/10 text-warning-text",
    labelKey: "pipelineStepFileUpload",
    hintKey: "pipelineFileUploadHint",
    templateToggleKey: null,
    numbered: false,
    copy: null,
  },
} as const satisfies Partial<Record<PipelineStepType, StepMetaShape>>

// Registry order, which is also the order steps are offered in.
export const CHANNEL_ORDER = Object.keys(CHANNELS) as ChannelSlug[]

const CHANNEL_BY_STEP = new Map<PipelineStepType, ChannelSlug>(
  CHANNEL_ORDER.flatMap((channel) =>
    (Object.keys(CHANNELS[channel].steps) as PipelineStepType[]).map(
      (type) => [type, channel] as const,
    ),
  ),
)

// The channel a step runs on, or null for a utility step (and no step).
export function channelForStep(
  type: PipelineStepType | "",
): ChannelSlug | null {
  return type === "" ? null : (CHANNEL_BY_STEP.get(type) ?? null)
}

// Whether a step runs on an add-on channel: the picker lists it under "Extras"
// and its card carries an "Extra" badge.
export function isExtraStep(type: PipelineStepType): boolean {
  const channel = channelForStep(type)
  return channel !== null && CHANNELS[channel].extra
}

// Plugin slug (as the backend manifest names it) to the campaign flag it sets.
const PLUGIN_FLAG_BY_SLUG: Record<ChannelPluginSlug, CampaignPluginFlag> = {
  socials: "socials_enabled",
  decision_makers: "decision_makers_enabled",
}

// The campaign flags the sequence's channels need on, each with the first
// channel asking for it. Static, so it works before (or without) the manifest;
// a loaded manifest adds whatever the server requires beyond it.
export function requiredPluginFlags(
  steps: PipelineStep[],
  manifest?: ChannelManifest,
): Map<CampaignPluginFlag, ChannelSlug> {
  const required = new Map<CampaignPluginFlag, ChannelSlug>()
  const channels = new Set(
    steps
      .map((step) => channelForStep(step.type))
      .filter((channel): channel is ChannelSlug => channel !== null),
  )
  for (const channel of CHANNEL_ORDER) {
    if (!channels.has(channel)) continue
    for (const flag of CHANNELS[channel].requiredPluginFlags) {
      if (!required.has(flag)) required.set(flag, channel)
    }
  }
  for (const entry of manifest?.channels ?? []) {
    if (!channels.has(entry.slug)) continue
    for (const slug of entry.required_plugins) {
      const flag = PLUGIN_FLAG_BY_SLUG[slug]
      if (!required.has(flag)) required.set(flag, entry.slug)
    }
  }
  return required
}
